<script setup lang="ts">
import { loadStripe } from '@stripe/stripe-js';
import { toTypedSchema } from '@vee-validate/zod';
import Checkbox from 'primevue/checkbox';
import DatePicker from 'primevue/datepicker';
import InputMask from 'primevue/inputmask';
import InputOtp from 'primevue/inputotp';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import Select from 'primevue/select';
import { useForm } from 'vee-validate';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import * as zod from 'zod';

import { Address } from '@/entities/address/lib/types';
import { useBookingQuoteStore } from '@/entities/quickQuote/lib/booking-store';
import InputAddress from '@/features/address/InputAddress.vue';
import PlateDecoder from '@/features/plateDecoder/PlateDecoder.vue';
import VinDecoder from '@/features/vinDecoder/VinDecoder.vue';
import { useMessages } from '@/shared/composables';
import { addDays } from '@/shared/helpers';
import { formatMoney } from '@/shared/helpers/formatters';
import { InputTextWithError } from '@/shared/ui';
import { LicensePlate } from '@/widgets/quickQuoteDialog/model/types';

import {
	confirmPhone,
	createEstimate,
	createEstimateVehicles,
	createPaymentIntent,
	createQuote,
	getEstimateErrorCode,
	getQuickQuoteSettings
} from './api';
import {
	dateKey,
	federalHolidayDates,
	Holiday,
	holidayByDate,
	holidayColor,
	weekdayName
} from './lib/holidays';
import {
	bookingThemeVars,
	DEFAULT_BOOKING_COLOR,
	isBookingColor
} from './lib/theme';
import BookingButton from './ui/BookingButton.vue';
import BookingCard from './ui/BookingCard.vue';
import BookingCardHeader from './ui/BookingCardHeader.vue';
import BookingErrorCard from './ui/BookingErrorCard.vue';
import BookingField from './ui/BookingField.vue';
import BookingItemPricing from './ui/BookingItemPricing.vue';
import BookingQuoteSummary from './ui/BookingQuoteSummary.vue';
import BookingVehicleCard from './ui/BookingVehicleCard.vue';

import type {
	CreateQuotePayload,
	EstimateVehicle
} from '@/entities/quickQuote/lib/booking-api.types';
import type {
	Stripe,
	StripeElements,
	StripePaymentElement
} from '@stripe/stripe-js';

const props = withDefaults(
	defineProps<{
		apiKey: string;
		closable?: boolean;
	}>(),
	{
		closable: false
	}
);

const emit = defineEmits<{
	close: [];
}>();

type BookingStep = 1 | 2 | 3;
type PhoneVerificationStage = 'vehicle' | 'phone' | 'code';
type VehicleEstimateRequest =
	| { vin: string }
	| { plate: string; state: string };

enum VehicleSearchError {
	GLASS_UNAVAILABLE = 'glass-unavailable',
	SERVICE_UNAVAILABLE = 'service-unavailable',
	VEHICLE_NOT_FOUND = 'vehicle-not-found'
}

const GLASS_UNAVAILABLE_ERROR_CODES = new Set([
	'WINDSHIELD_NOT_FOUND',
	'NAGS_PART_NOT_FOUND',
	'VENDOR_OFFER_NOT_FOUND'
]);

const steps: { value: BookingStep; label: string; icon: string }[] = [
	{ value: 1, label: 'Vehicle', icon: 'pi pi-car' },
	{ value: 2, label: 'Details', icon: 'pi pi-user' },
	{ value: 3, label: 'Confirm', icon: 'pi pi-calendar' }
];

const bookingStore = useBookingQuoteStore();
const toastMessages = useMessages();

const activeStep = ref<BookingStep>(1);
const settingsLoading = ref(true);
const settingsError = ref(false);
const isLoading = ref(false);
const isSubmitting = ref(false);
const submitted = ref(false);
const vehicleSearchError = ref<VehicleSearchError>();
const detailsValidationAttempted = ref(false);
const phoneVerificationStage = ref<PhoneVerificationStage>('vehicle');
const verificationVehicleRequest = ref<VehicleEstimateRequest>();
const verificationPhone = ref('');
const verificationCode = ref('');
const verificationError = ref('');
const phoneConfirmationLoading = ref(false);
const verifiedPhoneNumber = ref('');
const storeConsumerData = ref(false);
const estimateConsumerExists = ref(false);

const loadingMessageIndex = ref(0);
const loadingProgress = ref(8);
let loadingMessageInterval: ReturnType<typeof setInterval> | undefined;
let loadingProgressInterval: ReturnType<typeof setInterval> | undefined;
let abortCon: AbortController | undefined;

// --- Scheduling (last screen) ---
const scheduledDate = ref<Date | null>(null);
const notes = ref('');
const installTimeSpanId = ref<number | null>(null);

const STRIPE_PUBLISHABLE_KEY =
	'pk_live_51OMbKuJjPzVNKuc0OnesvqgcZqKK4aFCFyaa2lbobspne1yCbB0DJtU5F18cW6ZFP3iqLI4aKrZXrzfgpq6umVil004p2UJhR5';

const paymentElementContainer = ref<HTMLElement | null>(null);
const paymentSetupLoading = ref(false);
const paymentSetupError = ref<string | null>(null);
const stripeLoadError = ref(false);

let stripeConnection: Stripe | null = null;
let stripeConnectionAccountId: string | null = null;
let stripeElements: StripeElements | null = null;
let stripePaymentElement: StripePaymentElement | null = null;
let paymentIntentAmount: number | null = null;

// --- Details form ---
// Fields + validation mirror ContactInfoPhones / ContactInfoEmails /
// ContactInfoAddresses, but as a single instance each. First name is required.
const { t } = useI18n();

const detailsSchema = toTypedSchema(
	zod.object({
		first_name: zod.string({ message: t('required') }).min(1, t('required')),
		last_name: zod.string().optional(),
		phone: zod.object({
			number: zod
				.string({ message: t('required') })
				.min(14, t('errorNumber'))
				.max(14, t('errorNumber')),
			extension: zod.string().nullish()
		}),
		email: zod.string({ message: t('required') }).email(),
		address: zod.object({
			address: zod.string({ message: t('invalidAddress') }),
			city: zod.string().nullish(),
			extra: zod.string().nullish(),
			full_address: zod.string({ message: t('required') }),
			google_place_id: zod.string({ message: t('invalidAddress') }),
			lat: zod.number({ message: t('invalidAddress') }),
			lng: zod.number({ message: t('invalidAddress') }),
			po_box: zod.number().nullish(),
			state: zod.string().nullish(),
			unit: zod.string().nullish(),
			zip: zod.string().nullish()
		})
	})
);

const {
	defineField,
	errors,
	validate: validateDetailsForm,
	resetForm: resetDetailsForm,
	setValues: setDetailsValues,
	values: detailsValues
} = useForm({
	validationSchema: detailsSchema,
	initialValues: {
		first_name: '',
		last_name: '',
		phone: { number: '', extension: '' },
		email: '',
		address: {}
	}
});

const [firstName] = defineField('first_name');
const [lastName] = defineField('last_name');
const [phoneNumber] = defineField('phone.number');
const [phoneExtension] = defineField('phone.extension');
const [email] = defineField('email');
const [address] = defineField('address');

const getAddressError = () => {
	const addressFields = [
		'address.full_address',
		'address.google_place_id',
		'address.lat',
		'address.lng',
		'address.address'
	];
	for (const field of addressFields) {
		const message = errors.value[field as keyof typeof errors.value];
		if (message) return message;
	}
	return errors.value.address;
};

const hasCoordinates = (value: Partial<Address> | null | undefined) =>
	Boolean(
		value?.lat &&
			value?.lng &&
			Number.isFinite(Number(value.lat)) &&
			Number.isFinite(Number(value.lng))
	);

const getDistanceInMiles = (
	first: Pick<Address, 'lat' | 'lng'>,
	second: Pick<Address, 'lat' | 'lng'>
) => {
	const earthRadiusInMiles = 3958.8;
	const toRadians = (value: number) => (value * Math.PI) / 180;

	const firstLat = Number(first.lat);
	const secondLat = Number(second.lat);
	const deltaLat = toRadians(secondLat - firstLat);
	const deltaLng = toRadians(Number(second.lng) - Number(first.lng));

	const haversine =
		Math.sin(deltaLat / 2) ** 2 +
		Math.cos(toRadians(firstLat)) *
			Math.cos(toRadians(secondLat)) *
			Math.sin(deltaLng / 2) ** 2;

	return (
		2 *
		earthRadiusInMiles *
		Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
	);
};

const serviceRadiusError = computed(() => {
	if (
		!(
			bookingStore.requiredMobileFeeOption ||
			bookingStore.selectedExtraOptionKeys.includes('mobileFee')
		) ||
		!bookingStore.radiusIsTurned ||
		bookingStore.radiusInMiles == null
	)
		return undefined;
	if (!hasCoordinates(bookingStore.shopAddress)) return undefined;

	const consumerAddress = address.value as Partial<Address> | null | undefined;
	if (!consumerAddress?.full_address && !consumerAddress?.address)
		return undefined;

	if (!hasCoordinates(consumerAddress)) {
		return 'Customer address coordinates are required for mobile service';
	}

	const radiusInMiles = Number(bookingStore.radiusInMiles);
	if (!Number.isFinite(radiusInMiles)) return undefined;

	const distanceInMiles = getDistanceInMiles(
		bookingStore.shopAddress as Pick<Address, 'lat' | 'lng'>,
		consumerAddress as Pick<Address, 'lat' | 'lng'>
	);

	if (distanceInMiles > radiusInMiles) {
		return `Customer address is outside the ${bookingStore.radiusInMiles} mile mobile service radius`;
	}

	return undefined;
});

const addressError = computed(
	() =>
		serviceRadiusError.value ||
		(detailsValidationAttempted.value ? getAddressError() : undefined)
);

const loadingMessages = [
	'Reading vehicle data...',
	'Matching the glass options...',
	'Checking supplier availability...',
	'Comparing available prices...',
	'Building your booking estimate...'
];
const loadingMessage = computed(
	() => bookingStore.currentStep || loadingMessages[loadingMessageIndex.value]
);
const estimateLoadingNotice = ref('');
const loadingDescription = computed(
	() =>
		estimateLoadingNotice.value ||
		'Supplier searches can take up to 30 seconds. We are still working'
);

const themeStyle = computed(() => {
	const color = bookingStore.bookingColor;
	return bookingThemeVars(
		isBookingColor(color) ? color : DEFAULT_BOOKING_COLOR
	);
});

const currentStepIndex = computed(() =>
	steps.findIndex(step => step.value === activeStep.value)
);

const vehicles = computed(() => bookingStore.vehicles);

const pricingReady = computed(
	() =>
		!isLoading.value &&
		bookingStore.selectedVehicle &&
		bookingStore.quoteTotal !== null
);
const canContinueFromVehicle = computed(() => Boolean(pricingReady.value));
const canContinueFromDetails = computed(() => !serviceRadiusError.value);
const normalizedVerificationPhone = computed(() =>
	verificationPhone.value.replace(/\D/g, '')
);
const normalizedVerificationCode = computed(() =>
	verificationCode.value.replace(/\D/g, '').slice(0, 6)
);
const canConfirmPhone = computed(
	() =>
		normalizedVerificationPhone.value.length >= 10 &&
		!phoneConfirmationLoading.value
);
const canVerifyCode = computed(
	() => normalizedVerificationCode.value.length === 6 && !isLoading.value
);

const quoteTotal = computed(() => bookingStore.quoteTotal);
const hasQuote = computed(
	() => Boolean(bookingStore.selectedVehicle) && quoteTotal.value !== null
);
const showVehicleSearchResult = computed(
	() => bookingStore.vehicleSearchComplete || Boolean(vehicleSearchError.value)
);
const showPhoneVerification = computed(
	() =>
		bookingStore.phoneVerificationRequired &&
		phoneVerificationStage.value !== 'vehicle' &&
		Boolean(bookingStore.selectedVehicle) &&
		!isLoading.value &&
		!bookingStore.estimateId
);
const showStoreConsumerDataConsent = computed(
	() =>
		bookingStore.phoneVerificationRequired &&
		Boolean(verifiedPhoneNumber.value) &&
		!estimateConsumerExists.value
);
const vehicleSearchContent = computed(() => {
	switch (vehicleSearchError.value) {
		case VehicleSearchError.GLASS_UNAVAILABLE:
			return {
				title: 'Glass quote unavailable',
				description:
					"We found your vehicle, but couldn't find an available glass option online",
				hint: `Contact ${bookingStore.shopName} directly and they can help with your quote`
			};
		case VehicleSearchError.SERVICE_UNAVAILABLE:
			return {
				title: "We couldn't complete your search",
				description:
					'The quote service is unavailable right now. Please try again in a few moments',
				hint: `If the issue continues, contact ${bookingStore.shopName} for help`
			};
		default:
			return {
				title: 'No matching vehicle found',
				description:
					"We couldn't match that VIN or plate to a vehicle. Check the details and search again",
				hint: 'For a VIN, enter all 17 characters. For a plate, confirm the number and state are correct'
			};
	}
});
const paymentRequired = computed(() => bookingStore.paymentRequired);

const prepaymentAmountCents = computed(
	() => bookingStore.prepaymentAmountCents
);
const paymentStepLoading = computed(
	() => paymentRequired.value && paymentSetupLoading.value
);

const selectedVehicle = computed(() => bookingStore.selectedVehicle);
const vehicleLabel = computed(() => {
	const vehicle = bookingStore.selectedVehicle;
	if (!vehicle) return undefined;
	return [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');
});

const installTimeSpans = computed(() => bookingStore.installTimeSpans);
const timeSpanOptions = computed(() =>
	installTimeSpans.value.map(span => ({
		id: span.id,
		label: formatTimeSpan(span.name, span.time_start, span.time_end)
	}))
);

const disabledDays = computed(() => bookingStore.excludedWeekdays ?? []);

const disallowedDatesAfterToday = computed(() =>
	Array.from(
		{ length: bookingStore.disallowForDaysAfterToday + 1 },
		(_, index) => addDays(new Date(), index)
	)
);

const disabledDates = computed(() => {
	const holidays = bookingStore.excludeHolidays
		? federalHolidayDates.map(key => {
				const [year, month, day] = key.split('-').map(Number);
				return new Date(year, month - 1, day);
			})
		: [];

	return [...holidays, ...disallowedDatesAfterToday.value];
});

const holidayForCell = (cell: { year: number; month: number; day: number }) =>
	holidayByDate.get(dateKey(cell.year, cell.month + 1, cell.day));

const holidayCellStyle = (holiday: Holiday) => ({
	position: 'relative' as const,
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '100%',
	height: '100%',
	fontWeight: '700',
	color: holidayColor(holiday),
	pointerEvents: 'auto' as const,
	cursor: 'help'
});

const holidayDotStyle = (holiday: Holiday) => ({
	position: 'absolute' as const,
	bottom: '3px',
	left: '50%',
	transform: 'translateX(-50%)',
	width: '5px',
	height: '5px',
	borderRadius: '9999px',
	backgroundColor: holidayColor(holiday)
});

const closedWeekday = (cell: { year: number; month: number; day: number }) => {
	if (!disabledDays.value.length) return undefined;
	const weekday = new Date(cell.year, cell.month, cell.day).getDay();
	return disabledDays.value.includes(weekday) ? weekday : undefined;
};

const closedWeekdayLabel = (weekday: number) =>
	`We're closed on ${weekdayName(weekday)}s`;

const closedCellStyle = {
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '100%',
	height: '100%',
	pointerEvents: 'auto' as const,
	cursor: 'help'
};

// --- Stepper visual state ---
const nodeClass = (index: number) => {
	if (index < currentStepIndex.value) {
		return 'tw3-border-primary-500 tw3-bg-primary-500 tw3-text-white';
	}
	if (index === currentStepIndex.value) {
		return 'tw3-border-primary-500 tw3-bg-primary-50 tw3-text-primary-600 tw3-ring-4 tw3-ring-primary-100';
	}
	return 'tw3-border-surface-200 tw3-bg-surface-0 tw3-text-surface-400';
};
const labelClass = (index: number) => {
	if (index === currentStepIndex.value) return 'tw3-text-surface-900';
	if (index < currentStepIndex.value) return 'tw3-text-surface-700';
	return 'tw3-text-surface-400';
};

const formatTimeSpan = (
	name: string | null,
	start: string | null,
	end: string | null
) => {
	const formatTime = (value: string | null) => {
		if (!value) return null;

		const [hoursRaw, minutesRaw = '00'] = value.split(':');
		const hours = Number(hoursRaw);
		const minutes = Number(minutesRaw);

		if (Number.isNaN(hours) || Number.isNaN(minutes)) return value.slice(0, 5);

		const suffix = hours >= 12 ? 'PM' : 'AM';
		const displayHours = hours % 12 || 12;
		const displayMinutes = String(minutes).padStart(2, '0');

		return `${displayHours}:${displayMinutes} ${suffix}`;
	};

	const window = [formatTime(start), formatTime(end)]
		.filter(Boolean)
		.join(' - ');

	if (name && window) return `${name} (${window})`;
	return name || window || 'Time window';
};

const stopLoadingAnimation = () => {
	if (loadingMessageInterval) clearInterval(loadingMessageInterval);
	if (loadingProgressInterval) clearInterval(loadingProgressInterval);

	loadingMessageInterval = undefined;
	loadingProgressInterval = undefined;
};

const isPhoneVerificationNotRequiredError = (errorCode: string | undefined) =>
	errorCode === 'PHONE_VERIFICATION_NOT_REQUIRED';

const startLoadingAnimation = () => {
	stopLoadingAnimation();
	loadingMessageIndex.value = 0;
	loadingProgress.value = 8;

	loadingMessageInterval = setInterval(() => {
		loadingMessageIndex.value =
			(loadingMessageIndex.value + 1) % loadingMessages.length;
	}, 3500);

	loadingProgressInterval = setInterval(() => {
		loadingProgress.value =
			loadingProgress.value < 78
				? loadingProgress.value + 6
				: Math.min(92, loadingProgress.value + 1);
	}, 900);
};

const cancelEstimateRequest = () => {
	abortCon?.abort();
	abortCon = undefined;
	bookingStore.currentStep = undefined;
	estimateLoadingNotice.value = '';
	isLoading.value = false;
};

const settingsRestartError = 'Please start over before continuing.';

const resetPhoneVerification = () => {
	phoneVerificationStage.value = 'vehicle';
	verificationVehicleRequest.value = undefined;
	verificationPhone.value = '';
	verificationCode.value = '';
	verificationError.value = '';
	phoneConfirmationLoading.value = false;
	verifiedPhoneNumber.value = '';
	estimateConsumerExists.value = false;
	estimateLoadingNotice.value = '';
};

const showSettingsReloadRequiredError = async () => {
	verificationError.value = settingsRestartError;
	phoneVerificationStage.value = 'phone';
	await loadSettings();
};

const formatPhoneForDetails = (value: string) => {
	const digits = value.replace(/\D/g, '').slice(0, 10);
	if (digits.length !== 10) return value;
	return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const applyEstimateConsumer = (consumer: unknown) => {
	estimateConsumerExists.value = Boolean(
		consumer && typeof consumer === 'object'
	);
	if (!estimateConsumerExists.value) return;

	const data = consumer as Record<string, any>;
	const phoneValue =
		typeof data.phone_number === 'string'
			? data.phone_number.replace('+1', '')
			: typeof data.phone?.number === 'string'
				? data.phone.number.replace('+1', '')
				: '';

	setDetailsValues({
		first_name: typeof data.first_name === 'string' ? data.first_name : '',
		last_name: typeof data.last_name === 'string' ? data.last_name : '',
		phone: {
			number: formatPhoneForDetails(phoneValue || verifiedPhoneNumber.value),
			extension:
				typeof data.phone?.extension === 'string' ? data.phone.extension : ''
		},
		email: typeof data.email === 'string' ? data.email : '',
		address:
			data.address && typeof data.address === 'object'
				? (data.address as Record<string, unknown>)
				: {}
	});
};

const runEstimate = async (
	req:
		| { vin: string; phone?: string; phone_verification_code?: string }
		| {
				plate: string;
				state: string;
				phone?: string;
				phone_verification_code?: string;
		  },
	options: { notice?: string } = {}
) => {
	abortCon?.abort();
	const controller = new AbortController();
	abortCon = controller;
	vehicleSearchError.value = undefined;
	estimateLoadingNotice.value = options.notice ?? '';
	isLoading.value = true;

	try {
		bookingStore.currentStep = 'Building your booking estimate...';
		const data = await createEstimate(props.apiKey, req, controller.signal);
		const selectedVehicleKey = bookingStore.selectedVehicleKey;

		bookingStore.applyEstimate(data);
		if (
			selectedVehicleKey &&
			data.vehicles.some(vehicle => vehicle.key === selectedVehicleKey)
		) {
			bookingStore.selectedVehicleKey = selectedVehicleKey;
		}
		if ('phone' in req && req.phone) {
			verifiedPhoneNumber.value = req.phone;
			phoneNumber.value = formatPhoneForDetails(req.phone);
		}
		applyEstimateConsumer(data.consumer);
		if (estimateConsumerExists.value) storeConsumerData.value = false;

		if (!data.optimal?.length) {
			toastMessages.showWarning(
				'We could not find pricing for this glass. Please try again later or contact the shop directly.'
			);
		}
	} catch (error) {
		if (controller.signal.aborted) return;

		const errorCode = getEstimateErrorCode(error);
		if (errorCode === 'INVALID_PHONE_VERIFICATION_CODE') {
			verificationError.value = 'The verification code is incorrect.';
			phoneVerificationStage.value = 'code';
		} else if (errorCode === 'PHONE_VERIFICATION_REQUIRED_FOR_ESTIMATE') {
			await showSettingsReloadRequiredError();
		} else {
			bookingStore.resetEstimate();
			vehicleSearchError.value =
				errorCode === 'VIN_NOT_FOUND'
					? VehicleSearchError.VEHICLE_NOT_FOUND
					: errorCode && GLASS_UNAVAILABLE_ERROR_CODES.has(errorCode)
						? VehicleSearchError.GLASS_UNAVAILABLE
						: VehicleSearchError.SERVICE_UNAVAILABLE;
		}
	} finally {
		if (abortCon === controller) {
			bookingStore.currentStep = undefined;
			isLoading.value = false;
			abortCon = undefined;
		}
	}
};

const runVehicleSearch = async (req: VehicleEstimateRequest) => {
	abortCon?.abort();
	const controller = new AbortController();
	abortCon = controller;
	vehicleSearchError.value = undefined;
	estimateLoadingNotice.value = '';
	isLoading.value = true;

	try {
		bookingStore.currentStep = 'Finding your vehicle...';
		const data = await createEstimateVehicles(
			props.apiKey,
			req,
			controller.signal
		);

		bookingStore.applyEstimateVehicles(data.vehicles);
		verificationVehicleRequest.value = req;
		verificationPhone.value = '';
		verificationCode.value = '';
		verificationError.value = '';
		phoneVerificationStage.value = bookingStore.selectedVehicle
			? 'phone'
			: 'vehicle';
	} catch (error) {
		if (controller.signal.aborted) return;

		bookingStore.resetEstimate();
		const errorCode = getEstimateErrorCode(error);
		if (isPhoneVerificationNotRequiredError(errorCode)) {
			await loadSettings();
			await runEstimate(req);
			return;
		}

		vehicleSearchError.value =
			errorCode === 'VIN_NOT_FOUND'
				? VehicleSearchError.VEHICLE_NOT_FOUND
				: errorCode && GLASS_UNAVAILABLE_ERROR_CODES.has(errorCode)
					? VehicleSearchError.GLASS_UNAVAILABLE
					: VehicleSearchError.SERVICE_UNAVAILABLE;
	} finally {
		if (abortCon === controller) {
			bookingStore.currentStep = undefined;
			isLoading.value = false;
			abortCon = undefined;
		}
	}
};

const onVinSubmit = (vin: string) => {
	if (!bookingStore.phoneVerificationRequired) return runEstimate({ vin });

	bookingStore.resetEstimate();
	return runVehicleSearch({ vin });
};

const submitVerificationPhone = async () => {
	if (!canConfirmPhone.value) return;

	phoneConfirmationLoading.value = true;
	verificationError.value = '';

	try {
		await confirmPhone(props.apiKey, {
			phone: normalizedVerificationPhone.value
		});
		verificationPhone.value = normalizedVerificationPhone.value;
		verificationCode.value = '';
		phoneVerificationStage.value = 'code';
	} catch (error) {
		const errorCode = getEstimateErrorCode(error);
		if (isPhoneVerificationNotRequiredError(errorCode)) {
			await loadSettings();
			if (verificationVehicleRequest.value) {
				await runEstimate(verificationVehicleRequest.value, {
					notice:
						'Your shop no longer requires phone verification. We are building your estimate now.'
				});
			} else {
				await showSettingsReloadRequiredError();
			}
		} else {
			verificationError.value =
				'We could not send the verification code. Please try again.';
		}
	} finally {
		phoneConfirmationLoading.value = false;
	}
};

const submitVerificationCode = async () => {
	if (!canVerifyCode.value || !verificationVehicleRequest.value) return;

	verificationError.value = '';
	estimateLoadingNotice.value = '';
	bookingStore.currentStep = 'Building your booking estimate...';
	isLoading.value = true;
	await nextTick();

	await runEstimate({
		...verificationVehicleRequest.value,
		phone: normalizedVerificationPhone.value,
		phone_verification_code: normalizedVerificationCode.value
	});
};

const onPlateSubmit = async (plate: LicensePlate) => {
	if (bookingStore.phoneVerificationRequired) {
		bookingStore.resetEstimate();
		bookingStore.vehiclePlate = plate;
		return runVehicleSearch({
			plate: plate.number,
			state: plate.state
		});
	}

	bookingStore.currentStep = 'Identifying vehicle...';
	bookingStore.vehiclePlate = plate;
	await runEstimate({ plate: plate.number, state: plate.state });
};

const chooseVehicle = (vehicle: EstimateVehicle) => {
	bookingStore.selectVehicle(vehicle);
	if (bookingStore.phoneVerificationRequired && !bookingStore.estimateId) {
		phoneVerificationStage.value = 'phone';
		verificationError.value = '';
	}
};

const startOver = () => {
	cancelEstimateRequest();
	activeStep.value = 1;
	submitted.value = false;
	vehicleSearchError.value = undefined;
	resetPhoneVerification();
	storeConsumerData.value = false;
	estimateLoadingNotice.value = '';
	bookingStore.startOver();
	resetDetailsForm();
	scheduledDate.value = null;
	notes.value = '';
	installTimeSpanId.value = null;
	resetPayment();
};

const goNext = () => {
	const nextStep = steps[currentStepIndex.value + 1];
	if (nextStep) activeStep.value = nextStep.value;
};

const canGoBack = computed(() => {
	if (submitted.value) return false;
	if (activeStep.value > 1) return true;
	if (phoneVerificationStage.value !== 'vehicle') return true;
	return vehicles.value.length > 1 && Boolean(bookingStore.selectedVehicle);
});

const goBack = () => {
	if (activeStep.value === 1 && phoneVerificationStage.value === 'code') {
		phoneVerificationStage.value = 'phone';
		verificationError.value = '';
		return;
	}
	if (activeStep.value === 1 && phoneVerificationStage.value === 'phone') {
		resetPhoneVerification();
		return;
	}

	if (activeStep.value === 1) {
		if (vehicles.value.length > 1 && bookingStore.selectedVehicle) {
			bookingStore.selectedVehicleKey = undefined;
		}
		return;
	}

	const previousStep = steps[currentStepIndex.value - 1];
	if (previousStep) activeStep.value = previousStep.value;
};

const continueFromDetails = async () => {
	detailsValidationAttempted.value = true;
	const { valid } = await validateDetailsForm();
	if (!valid || serviceRadiusError.value) {
		activeStep.value = 2;
		return;
	}

	goNext();
};

const ADDRESS_KEYS: (keyof Address)[] = [
	'address',
	'city',
	'extra',
	'full_address',
	'google_place_id',
	'lat',
	'lng',
	'po_box',
	'state',
	'unit',
	'zip'
];

const buildAddressPayload = (
	value: Record<string, unknown> | null | undefined
) => {
	if (!value?.full_address && !value?.address) return null;

	const payload: Record<string, unknown> = {};
	for (const key of ADDRESS_KEYS) {
		const fieldValue = value[key];
		if (fieldValue !== undefined) payload[key] = fieldValue;
	}
	return payload as Partial<Address>;
};

const toDateString = (date: Date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const rgbChannels = (channels: string, fallback: string) => {
	const value = channels.trim();
	return value ? `rgb(${value.split(/\s+/).join(', ')})` : fallback;
};

const resolveThemeColor = (variable: string, fallback: string) => {
	const themed = themeStyle.value[variable];
	if (themed) {
		return rgbChannels(themed, fallback);
	}
	const el = paymentElementContainer.value ?? document.documentElement;
	return rgbChannels(getComputedStyle(el).getPropertyValue(variable), fallback);
};

const teardownPaymentElement = () => {
	stripePaymentElement?.destroy();
	stripePaymentElement = null;
	stripeElements = null;
};

const resetPayment = () => {
	teardownPaymentElement();
	paymentIntentAmount = null;
	stripeLoadError.value = false;
	paymentSetupError.value = null;
};

const setupPayment = async () => {
	const amount = prepaymentAmountCents.value;
	if (
		paymentSetupLoading.value ||
		!paymentRequired.value ||
		!bookingStore.estimateId ||
		amount === null
	) {
		return;
	}

	// The mounted form still matches the current prepayment total — nothing to redo.
	if (
		stripePaymentElement &&
		paymentIntentAmount === amount &&
		!stripeLoadError.value &&
		!paymentSetupError.value
	) {
		return;
	}

	paymentSetupLoading.value = true;
	paymentSetupError.value = null;
	stripeLoadError.value = false;
	teardownPaymentElement();

	try {
		const intent = await createPaymentIntent(props.apiKey, {
			estimateId: bookingStore.estimateId,
			amount
		});
		paymentIntentAmount = amount;

		const accountId = intent.stripeAccountId ?? null;
		if (!stripeConnection || stripeConnectionAccountId !== accountId) {
			stripeConnection = await loadStripe(
				STRIPE_PUBLISHABLE_KEY,
				accountId ? { stripeAccount: accountId } : undefined
			);
			stripeConnectionAccountId = accountId;
		}

		if (!stripeConnection) {
			stripeLoadError.value = true;
			return;
		}

		stripeElements = stripeConnection.elements({
			clientSecret: intent.clientSecret,
			appearance: {
				theme: 'stripe',
				variables: {
					colorPrimary: resolveThemeColor('--primary-600', 'rgb(5, 150, 105)'),
					colorText: resolveThemeColor('--surface-900', 'rgb(15, 23, 42)'),
					colorTextSecondary: resolveThemeColor(
						'--surface-500',
						'rgb(100, 116, 139)'
					)
				},
				rules: {
					'.AccordionItem': { borderRadius: '16px' },
					'.Input': { borderRadius: '8px' }
				}
			}
		});

		stripePaymentElement = stripeElements.create('payment');
		stripePaymentElement.on('loaderror', () => {
			stripeLoadError.value = true;
		});

		paymentSetupLoading.value = false;
		await nextTick();
		if (paymentElementContainer.value) {
			stripePaymentElement.mount(paymentElementContainer.value);
		}
	} catch (error) {
		paymentIntentAmount = null;
		paymentSetupError.value =
			(error as { response?: { data?: { message?: string } } })?.response?.data
				?.message ??
			(error as Error)?.message ??
			'We could not initialize the payment form.';
	} finally {
		paymentSetupLoading.value = false;
	}
};

const authorizePayment = async (): Promise<string | null> => {
	if (!stripeConnection || !stripeElements) {
		toastMessages.showError('The payment form is not ready yet.');
		return null;
	}

	try {
		const { error, paymentIntent } = await stripeConnection.confirmPayment({
			elements: stripeElements,
			confirmParams: { return_url: window.location.href },
			redirect: 'if_required'
		});

		if (error || !paymentIntent) {
			toastMessages.showError(error?.message || 'Payment failed');
			return null;
		}

		return paymentIntent.id;
	} catch (error) {
		toastMessages.showError((error as Error)?.message || 'Payment failed');
		return null;
	}
};

watch([activeStep, prepaymentAmountCents], ([step]) => {
	if (step === 3 && paymentRequired.value) setupPayment();
});

const submitBooking = async () => {
	if (!bookingStore.estimateId || !bookingStore.selectedVehicleKey) {
		return;
	}

	detailsValidationAttempted.value = true;
	const { valid } = await validateDetailsForm();
	if (!valid || serviceRadiusError.value) {
		activeStep.value = 2;
		return;
	}

	isSubmitting.value = true;

	try {
		// Payment gate: the job is only created after the card is authorized.
		let quotePaymentIntentId: string | null = null;
		if (paymentRequired.value) {
			quotePaymentIntentId = await authorizePayment();
			if (!quotePaymentIntentId) return;
		}

		const basePayload = {
			estimateId: bookingStore.estimateId,
			vehicleKey: bookingStore.selectedVehicleKey,
			selectedOptions: bookingStore.selectedOptionKeys,
			consumer: {
				first_name: (detailsValues.first_name ?? '').trim(),
				last_name: detailsValues.last_name?.trim() || null,
				phone: {
					number: (detailsValues.phone?.number ?? '').replace(/\D/g, ''),
					extension: detailsValues.phone?.extension?.trim() || null
				},
				email: (detailsValues.email ?? '').trim() || null,
				address: buildAddressPayload(
					detailsValues.address as Record<string, unknown>
				)
			},
			store_consumer_data: showStoreConsumerDataConsent.value
				? storeConsumerData.value
				: undefined,
			description: notes.value.trim() || null,
			install_time_span_id: installTimeSpanId.value ?? null,
			scheduled_date: scheduledDate.value
				? toDateString(scheduledDate.value)
				: null,
			paymentIntentId: quotePaymentIntentId
		};
		const payload: CreateQuotePayload =
			bookingStore.selectedGlassOption === 'repair'
				? {
						...basePayload,
						selectedGlassOption: 'repair',
						chipCount: bookingStore.chipCount
					}
				: {
						...basePayload,
						selectedGlassOption: 'replace'
					};

		try {
			const response = await createQuote(props.apiKey, payload);
			if (response.final_redirect_url && !props.closable)
				return window.location.assign(response.final_redirect_url);

			submitted.value = true;
		} catch (error) {
			toastMessages.showError(error);
			if (quotePaymentIntentId) {
				resetPayment();
				setupPayment();
			}
		}
	} finally {
		isSubmitting.value = false;
	}
};

const loadSettings = async () => {
	settingsLoading.value = true;
	settingsError.value = false;
	try {
		const settings = await getQuickQuoteSettings(props.apiKey);
		bookingStore.applyQuoteSettings(settings);
	} catch {
		settingsError.value = true;
	} finally {
		settingsLoading.value = false;
	}
};

onMounted(async () => {
	bookingStore.startOver();
	await loadSettings();
});

onUnmounted(() => {
	cancelEstimateRequest();
	stopLoadingAnimation();
});

watch(isLoading, loading => {
	if (loading) {
		startLoadingAnimation();
		return;
	}

	loadingProgress.value = 100;
	stopLoadingAnimation();
});

watch(
	() =>
		JSON.stringify({
			first_name: detailsValues.first_name,
			last_name: detailsValues.last_name,
			phone: detailsValues.phone,
			email: detailsValues.email,
			address: detailsValues.address
		}),
	(newValue, oldValue) => {
		if (
			oldValue !== undefined &&
			newValue !== oldValue &&
			storeConsumerData.value
		) {
			storeConsumerData.value = false;
		}
	}
);
</script>

<template>
	<div
		class="booking-bg tw3-flex tw3-min-h-screen tw3-flex-col tw3-text-surface-900"
		:style="themeStyle"
	>
		<BookingButton
			v-if="props.closable && (settingsLoading || settingsError)"
			class="tw3-fixed tw3-right-4 tw3-top-4 tw3-z-30"
			icon="pi pi-times"
			label="Close"
			size="small"
			variant="secondary"
			@click="emit('close')"
		/>

		<div
			v-if="settingsLoading"
			class="tw3-flex tw3-min-h-screen tw3-flex-1 tw3-flex-col tw3-items-center tw3-justify-center tw3-gap-4"
		>
			<ProgressSpinner class="tw3-size-14" strokeWidth="3" />
			<p class="tw3-text-sm tw3-text-surface-500">Loading...</p>
		</div>

		<main
			v-else-if="settingsError"
			class="tw3-mx-auto tw3-flex tw3-w-full tw3-max-w-5xl tw3-flex-1 tw3-items-center tw3-justify-center tw3-px-4 tw3-py-12 sm:tw3-px-6"
		>
			<BookingErrorCard
				actionLabel="Try again"
				:bookingPhone="bookingStore.bookingPhone"
				class="tw3-w-full tw3-max-w-2xl"
				description="Something went wrong while loading the booking settings. Please try again in a few moments"
				eyebrow="Booking unavailable"
				title="We couldn't load this booking page"
				@action="loadSettings"
			/>
		</main>

		<main
			v-else-if="!bookingStore.isTurned || !bookingStore.isPriceLevelExist"
			class="tw3-mx-auto tw3-flex tw3-w-full tw3-max-w-5xl tw3-flex-1 tw3-items-center tw3-justify-center tw3-px-4 tw3-py-12 sm:tw3-px-6"
		>
			<BookingErrorCard
				actionLabel="Contact shop manager"
				:bookingPhone="bookingStore.bookingPhone"
				class="tw3-w-full tw3-max-w-2xl"
				description="Quick Quote doesn’t appear to be fully configured for this shop"
				eyebrow="Booking unavailable"
				title="We couldn't load this booking page"
				@action="loadSettings"
			/>
		</main>

		<template v-else>
			<!-- Header -->
			<header
				class="booking-blurbar tw3-sticky tw3-top-0 tw3-z-20 tw3-border-b tw3-border-surface-200"
			>
				<div
					class="tw3-mx-auto tw3-flex tw3-max-w-5xl tw3-items-center tw3-justify-between tw3-gap-3 tw3-px-4 tw3-py-4 sm:tw3-px-6 sm:tw3-py-5"
				>
					<div class="tw3-flex tw3-min-w-0 tw3-items-center tw3-gap-3">
						<div
							class="booking-logo tw3-flex tw3-size-10 tw3-shrink-0 tw3-items-center tw3-justify-center tw3-rounded-xl tw3-text-xl tw3-text-white"
						>
							<i class="pi pi-car" />
						</div>
						<div class="tw3-min-w-0">
							<h1
								class="tw3-text-lg tw3-font-bold tw3-leading-tight sm:tw3-text-xl"
							>
								{{ bookingStore.shopName }}
							</h1>
							<p
								class="tw3-mt-0.5 tw3-flex tw3-items-center tw3-text-xs tw3-text-surface-500"
							>
								<i class="pi pi-shield-check tw3-shrink-0 tw3-text-[0.7rem]" />
								<span class="tw3-truncate">
									Fast, transparent quotes and bookings
								</span>
							</p>
						</div>
					</div>

					<div
						v-if="
							((showVehicleSearchResult ||
								showPhoneVerification ||
								isLoading) &&
								!submitted) ||
							props.closable
						"
						class="tw3-flex tw3-shrink-0 tw3-items-center tw3-gap-2"
					>
						<BookingButton
							v-if="
								(showVehicleSearchResult ||
									showPhoneVerification ||
									isLoading) &&
								!submitted
							"
							icon="pi pi-refresh"
							label="Start over"
							size="small"
							variant="secondary"
							@click="startOver"
						/>
						<BookingButton
							v-if="props.closable"
							icon="pi pi-times"
							label="Close"
							size="small"
							variant="secondary"
							@click="emit('close')"
						/>
					</div>
				</div>
			</header>

			<main
				class="tw3-mx-auto tw3-flex tw3-w-full tw3-max-w-5xl tw3-flex-1 tw3-flex-col tw3-px-4 sm:tw3-px-6"
				:class="
					props.closable
						? 'tw3-pb-[6rem] tw3-pt-4 sm:tw3-pt-6'
						: 'tw3-pb-32 tw3-pt-8'
				"
			>
				<!-- ============ SUCCESS ============ -->
				<section
					v-if="submitted"
					class="tw3-flex tw3-min-h-80 tw3-flex-1 tw3-flex-col tw3-items-center tw3-justify-center tw3-gap-5 tw3-text-center"
				>
					<div
						class="tw3-flex tw3-size-20 tw3-items-center tw3-justify-center tw3-rounded-full tw3-bg-primary-50 tw3-text-primary-600"
					>
						<i class="pi pi-check-circle !tw3-text-5xl" />
					</div>
					<div class="tw3-max-w-md">
						<h2 class="tw3-text-2xl tw3-font-bold">Booking confirmed</h2>
						<p class="tw3-mt-2 tw3-text-surface-500">
							Thanks! Your booking request has been received. The shop will
							reach out shortly to confirm the appointment.
						</p>
					</div>
					<BookingButton
						icon="pi pi-plus"
						label="Start a new booking"
						variant="secondary"
						@click="startOver"
					/>
				</section>

				<template v-else>
					<!-- Stepper -->
					<nav
						class="tw3-mx-auto tw3-mb-10 tw3-flex tw3-w-full tw3-max-w-[15rem] tw3-items-center tw3-justify-center sm:tw3-max-w-sm lg:tw3-max-w-md"
					>
						<template v-for="(step, index) in steps" :key="step.value">
							<div class="tw3-flex tw3-flex-col tw3-items-center tw3-gap-2">
								<div
									class="tw3-flex tw3-size-11 tw3-items-center tw3-justify-center tw3-rounded-full tw3-border-2 tw3-text-lg tw3-transition-all"
									:class="nodeClass(index)"
								>
									<i v-if="currentStepIndex > index" class="pi pi-check" />
									<i v-else :class="step.icon" />
								</div>
								<span
									class="tw3-text-sm tw3-font-semibold tw3-transition-colors"
									:class="labelClass(index)"
								>
									{{ step.label }}
								</span>
							</div>
							<div
								v-if="index < steps.length - 1"
								class="tw3-mb-8 tw3-h-0.5 tw3-flex-1 tw3-rounded-full tw3-transition-colors"
								:class="
									currentStepIndex > index
										? 'tw3-bg-primary-500'
										: 'tw3-bg-surface-200'
								"
							/>
						</template>
					</nav>

					<!-- ============ STEP 1: VEHICLE ============ -->
					<section v-show="activeStep === 1" class="tw3-space-y-6">
						<!-- Loading quote -->
						<div v-if="isLoading" class="tw3-space-y-6">
							<BookingVehicleCard
								v-if="bookingStore.selectedVehicle"
								:vehicle="bookingStore.selectedVehicle"
							/>

							<BookingCard
								class="tw3-flex tw3-min-h-80 tw3-flex-col tw3-items-center tw3-justify-center tw3-gap-5 tw3-px-6 tw3-py-12 tw3-text-center"
							>
								<div
									class="tw3-relative tw3-flex tw3-items-center tw3-justify-center"
								>
									<ProgressSpinner class="tw3-size-14" strokeWidth="3" />
									<i
										class="pi pi-car tw3-absolute tw3-text-2xl tw3-text-primary-600"
									/>
								</div>
								<div class="tw3-w-full tw3-max-w-md">
									<h2 class="tw3-text-lg tw3-font-semibold">
										{{ loadingMessage }}
									</h2>
									<p class="tw3-mt-1 tw3-text-sm tw3-text-surface-500">
										{{ loadingDescription }}
									</p>
									<div
										class="tw3-mt-5 tw3-h-2 tw3-overflow-hidden tw3-rounded-full tw3-bg-surface-200"
									>
										<div
											class="booking-progress-fill tw3-h-full tw3-rounded-full tw3-transition-all"
											:style="{ width: loadingProgress + '%' }"
										/>
									</div>
									<div
										class="tw3-mt-3 tw3-flex tw3-justify-between tw3-text-xs tw3-font-medium tw3-text-surface-400"
									>
										<span>Vehicle</span>
										<span>Glass</span>
										<span>Vendors</span>
										<span>Price</span>
									</div>
								</div>
							</BookingCard>
						</div>

						<!-- Content -->
						<template v-else>
							<!-- After search -->
							<div v-if="showPhoneVerification" class="tw3-space-y-6">
								<BookingVehicleCard
									v-if="bookingStore.selectedVehicle"
									:vehicle="bookingStore.selectedVehicle"
								/>

								<BookingCard class="tw3-mx-auto tw3-max-w-xl tw3-p-6">
									<div class="tw3-flex tw3-items-start tw3-gap-4">
										<div
											class="tw3-flex tw3-size-11 tw3-shrink-0 tw3-items-center tw3-justify-center tw3-rounded-xl tw3-bg-primary-50 tw3-text-xl tw3-text-primary-600"
										>
											<i class="pi pi-mobile" />
										</div>
										<div class="tw3-min-w-0">
											<h2 class="tw3-text-xl tw3-font-bold">
												Verify your phone
											</h2>
											<p class="tw3-mt-1 tw3-text-sm tw3-text-surface-500">
												Enter your phone number to receive a 6-digit
												verification code before we build your estimate.
											</p>
										</div>
									</div>

									<div class="tw3-mt-6 tw3-grid tw3-gap-4">
										<label
											v-if="phoneVerificationStage === 'phone'"
											class="tw3-grid tw3-gap-1.5"
										>
											<span
												class="tw3-text-sm tw3-font-semibold tw3-text-surface-700"
											>
												Phone number
											</span>
											<InputMask
												v-model="verificationPhone"
												autocomplete="tel"
												fluid
												mask="(999) 999-9999"
												placeholder="(555) 555-5555"
												type="text"
											/>
										</label>

										<label v-else class="tw3-grid tw3-gap-2">
											<span
												class="tw3-text-sm tw3-font-semibold tw3-text-surface-700"
											>
												6-digit verification code
											</span>
											<InputOtp
												v-model="verificationCode"
												class="tw3-justify-center"
												integerOnly
												:invalid="!!verificationError"
												:length="6"
											/>
											<div
												class="tw3-flex tw3-items-center tw3-justify-between tw3-gap-3"
											>
												<span class="tw3-text-xs tw3-text-surface-500">
													{{ normalizedVerificationCode.length }}/6 digits
												</span>
												<button
													class="tw3-border-0 tw3-bg-transparent tw3-p-0 tw3-text-xs tw3-font-semibold tw3-text-primary-600"
													type="button"
													@click="phoneVerificationStage = 'phone'"
												>
													Change phone
												</button>
											</div>
										</label>

										<p
											v-if="verificationError"
											class="tw3-m-0 tw3-rounded-lg tw3-bg-red-50 tw3-p-3 tw3-text-sm tw3-font-medium tw3-text-red-700"
										>
											{{ verificationError }}
										</p>

										<BookingButton
											v-if="phoneVerificationStage === 'phone'"
											:disabled="!canConfirmPhone"
											icon="pi pi-send"
											iconPos="right"
											label="Send code"
											:loading="phoneConfirmationLoading"
											@click="submitVerificationPhone"
										/>
										<BookingButton
											v-else
											:disabled="!canVerifyCode"
											icon="pi pi-check"
											iconPos="right"
											label="Verify and get estimate"
											:loading="isLoading"
											@click="submitVerificationCode"
										/>
									</div>
								</BookingCard>
							</div>

							<div v-else-if="showVehicleSearchResult" class="tw3-space-y-6">
								<BookingVehicleCard
									v-if="bookingStore.selectedVehicle"
									:vehicle="bookingStore.selectedVehicle"
								/>

								<div v-else-if="vehicles.length" class="tw3-space-y-4">
									<div>
										<h2 class="tw3-text-lg tw3-font-semibold">
											Select your vehicle
										</h2>
										<p class="tw3-mt-0.5 tw3-text-sm tw3-text-surface-500">
											We found a few matches &mdash; pick the one that fits
										</p>
									</div>
									<div
										v-for="vehicle in vehicles"
										:key="vehicle.key"
										class="tw3-cursor-pointer tw3-rounded-2xl tw3-transition-all hover:-tw3-translate-y-0.5 hover:tw3-shadow-lg"
										@click="chooseVehicle(vehicle)"
									>
										<BookingVehicleCard :vehicle="vehicle" />
									</div>
								</div>

								<BookingErrorCard
									v-else
									actionLabel="Try another search"
									:bookingPhone="bookingStore.bookingPhone"
									:description="vehicleSearchContent.description"
									eyebrow="Vehicle lookup"
									:hint="vehicleSearchContent.hint"
									:title="vehicleSearchContent.title"
									@action="startOver"
								/>
							</div>

							<!-- Search form -->
							<div v-else class="tw3-space-y-6">
								<div class="tw3-text-center">
									<h2 class="tw3-text-2xl tw3-font-bold">Find your vehicle</h2>
									<p
										class="tw3-mx-auto tw3-mt-2 tw3-max-w-md tw3-text-sm tw3-text-surface-500"
									>
										{{
											bookingStore.phoneVerificationRequired
												? 'Enter either the VIN or the license plate to start a secure quote with phone verification'
												: 'Enter either the VIN or the license plate to get an instant quote. You only need one'
										}}
									</p>
								</div>

								<div
									class="tw3-grid tw3-gap-4 md:tw3-grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:tw3-items-stretch"
								>
									<BookingCard class="tw3-flex tw3-flex-col tw3-gap-5 tw3-p-6">
										<div class="tw3-flex tw3-items-center tw3-gap-3">
											<div
												class="tw3-flex tw3-size-10 tw3-items-center tw3-justify-center tw3-rounded-xl tw3-bg-primary-50 tw3-text-lg tw3-text-primary-600"
											>
												<i class="pi pi-hashtag" />
											</div>
											<div>
												<h3 class="tw3-font-semibold">Enter VIN</h3>
												<p class="tw3-text-xs tw3-text-surface-500">
													17-character vehicle ID
												</p>
											</div>
										</div>
										<VinDecoder @submit="onVinSubmit" />
									</BookingCard>

									<div class="tw3-flex tw3-items-center tw3-justify-center">
										<span
											class="tw3-flex tw3-size-9 tw3-items-center tw3-justify-center tw3-rounded-full tw3-border tw3-border-surface-200 tw3-bg-surface-0 tw3-text-xs tw3-font-bold tw3-uppercase tw3-text-surface-400"
										>
											or
										</span>
									</div>

									<BookingCard class="tw3-flex tw3-flex-col tw3-gap-5 tw3-p-6">
										<div class="tw3-flex tw3-items-center tw3-gap-3">
											<div
												class="tw3-flex tw3-size-10 tw3-items-center tw3-justify-center tw3-rounded-xl tw3-bg-primary-50 tw3-text-lg tw3-text-primary-600"
											>
												<i class="pi pi-id-card" />
											</div>
											<div>
												<h3 class="tw3-font-semibold">Enter license plate</h3>
												<p class="tw3-text-xs tw3-text-surface-500">
													Plate number and state
												</p>
											</div>
										</div>
										<PlateDecoder @submit="onPlateSubmit" />
									</BookingCard>
								</div>
							</div>

							<!-- Glass options / pricing -->
							<BookingCard v-if="pricingReady" class="tw3-p-6">
								<BookingCardHeader
									subtitle="Tap to add services. Your total updates instantly."
									title="Glass & options"
								/>
								<BookingItemPricing />
							</BookingCard>
						</template>
					</section>

					<!-- ============ STEP 2: DETAILS ============ -->
					<section
						v-show="activeStep === 2"
						class="tw3-grid tw3-gap-6 lg:tw3-grid-cols-[minmax(0,1fr)_20rem] lg:tw3-items-start"
					>
						<div class="tw3-space-y-6">
							<BookingCard class="tw3-p-6">
								<BookingCardHeader
									icon="pi pi-user"
									subtitle="We will use this to confirm the appointment."
									title="Contact information"
								/>

								<div
									class="tw3-grid tw3-items-start tw3-gap-4 md:tw3-grid-cols-2"
								>
									<BookingField
										v-model="firstName"
										autocomplete="given-name"
										:error="errors['first_name']"
										label="First name"
										placeholder="First name"
									/>
									<BookingField
										v-model="lastName"
										autocomplete="family-name"
										label="Last name"
										placeholder="Last name"
									/>

									<label class="tw3-grid tw3-gap-1.5 md:tw3-col-span-2">
										<span
											class="tw3-text-sm tw3-font-semibold tw3-text-surface-700"
										>
											Phone
										</span>
										<div class="tw3-flex tw3-gap-2">
											<InputMask
												v-model="phoneNumber"
												v-tooltip.top="{
													value: errors['phone.number'],
													pt: {
														text: {
															style: { backgroundColor: 'rgb(248 113 113)' }
														},
														arrow: {
															style: { borderTopColor: 'rgb(248 113 113)' }
														}
													}
												}"
												autocomplete="tel"
												class="tw3-flex-1"
												:disabled="!!verifiedPhoneNumber"
												fluid
												:invalid="!!errors['phone.number']"
												mask="(999) 999-9999"
												placeholder="Phone"
												type="text"
											/>
											<InputText
												v-model="phoneExtension"
												class="tw3-w-24"
												:disabled="!!verifiedPhoneNumber"
												maxlength="8"
												placeholder="Ext"
												type="text"
											/>
										</div>
									</label>

									<label class="tw3-grid tw3-gap-1.5 md:tw3-col-span-2">
										<span
											class="tw3-text-sm tw3-font-semibold tw3-text-surface-700"
										>
											Email
										</span>
										<InputTextWithError
											v-model="email"
											class="tw3-w-full"
											:errMsg="errors['email']"
											placeholder="name@example.com"
											type="email"
										/>
									</label>
								</div>
							</BookingCard>

							<BookingCard class="tw3-p-6">
								<BookingCardHeader
									icon="pi pi-map-marker"
									subtitle="Tell us where and when the service should happen."
									title="Service details"
								/>

								<div class="tw3-grid tw3-gap-4">
									<label class="tw3-grid tw3-gap-1.5">
										<span
											class="tw3-text-sm tw3-font-semibold tw3-text-surface-700"
										>
											Service address
										</span>
										<InputAddress
											:errMsg="addressError"
											:modelValue="address as Partial<Address>"
											withExtra
											withPoBox
											@update:model-value="value => (address = value)"
										/>
									</label>

									<label class="tw3-grid tw3-gap-1.5">
										<span
											class="tw3-text-sm tw3-font-semibold tw3-text-surface-700"
										>
											Preferred date
										</span>
										<DatePicker
											v-model="scheduledDate"
											dateFormat="mm/dd/yy"
											:disabledDates="disabledDates"
											:disabledDays="disabledDays"
											fluid
											:minDate="new Date()"
											placeholder="Select a date"
											showIcon
										>
											<template #date="{ date }">
												<span
													v-if="holidayForCell(date)"
													v-tooltip.top="holidayForCell(date)!.name"
													:style="holidayCellStyle(holidayForCell(date)!)"
												>
													{{ date.day }}
													<span
														:style="holidayDotStyle(holidayForCell(date)!)"
													/>
												</span>
												<span
													v-else-if="closedWeekday(date) !== undefined"
													v-tooltip.top="
														closedWeekdayLabel(closedWeekday(date)!)
													"
													:style="closedCellStyle"
												>
													{{ date.day }}
												</span>
												<template v-else>{{ date.day }}</template>
											</template>
										</DatePicker>
									</label>

									<label class="tw3-grid tw3-gap-1.5">
										<span
											class="tw3-text-sm tw3-font-semibold tw3-text-surface-700"
										>
											Installation time window
										</span>
										<Select
											v-if="timeSpanOptions.length"
											v-model="installTimeSpanId"
											optionLabel="label"
											:options="timeSpanOptions"
											optionValue="id"
											placeholder="Select a time window"
											showClear
										/>
										<p v-else class="tw3-m-0 tw3-text-sm tw3-text-surface-500">
											No specific time windows are configured. The shop will
											coordinate a time with you directly
										</p>
									</label>

									<BookingField
										v-model="notes"
										label="Notes"
										placeholder="Gate code, parking notes, glass damage details..."
										:rows="4"
										type="textarea"
									/>
								</div>
							</BookingCard>

							<BookingCard v-if="showStoreConsumerDataConsent" class="tw3-p-6">
								<label class="tw3-flex tw3-items-start tw3-gap-3">
									<Checkbox
										v-model="storeConsumerData"
										binary
										inputId="storeConsumerData"
									/>
									<span class="tw3-grid tw3-gap-1">
										<span
											class="tw3-text-sm tw3-font-semibold tw3-text-surface-800"
										>
											Save my personal details for future use
										</span>
										<span class="tw3-text-sm tw3-text-surface-500">
											Allow this shop to securely save your contact and address
											information so future bookings can be faster.
										</span>
									</span>
								</label>
							</BookingCard>
						</div>

						<aside class="lg:tw3-sticky lg:tw3-top-24">
							<BookingQuoteSummary />
						</aside>
					</section>

					<!-- ============ STEP 3: CONFIRM ============ -->
					<section
						v-show="activeStep === 3"
						class="tw3-grid tw3-gap-6 lg:tw3-grid-cols-[minmax(0,1fr)_20rem] lg:tw3-items-start"
					>
						<BookingCard
							v-if="paymentStepLoading"
							class="tw3-flex tw3-min-h-80 tw3-flex-col tw3-items-center tw3-justify-center tw3-gap-5 tw3-px-6 tw3-py-12 tw3-text-center lg:tw3-col-span-2"
						>
							<div
								class="tw3-relative tw3-flex tw3-items-center tw3-justify-center"
							>
								<ProgressSpinner class="tw3-size-14" strokeWidth="3" />
								<i
									class="pi pi-credit-card tw3-absolute tw3-text-2xl tw3-text-primary-600"
								/>
							</div>
							<div class="tw3-max-w-md">
								<h2 class="tw3-text-lg tw3-font-semibold">
									Preparing secure payment...
								</h2>
								<p class="tw3-mt-1 tw3-text-sm tw3-text-surface-500">
									Connecting to Stripe. This only takes a moment
								</p>
							</div>
						</BookingCard>

						<template v-else>
							<div class="tw3-space-y-6">
								<template v-if="paymentRequired">
									<BookingCard
										v-if="stripeLoadError || paymentSetupError"
										class="tw3-flex tw3-min-h-80 tw3-flex-col tw3-items-center tw3-justify-center tw3-gap-3 tw3-p-6 tw3-text-center"
									>
										<p
											class="tw3-m-0 tw3-max-w-md tw3-text-sm tw3-text-red-600"
										>
											{{
												paymentSetupError ||
												'We could not load the payment form. This is sometimes caused by browser extensions — try disabling them or use a different browser.'
											}}
										</p>
										<BookingButton
											icon="pi pi-refresh"
											label="Try again"
											size="small"
											variant="secondary"
											@click="setupPayment"
										/>
									</BookingCard>

									<div ref="paymentElementContainer" />
								</template>

								<BookingCard class="tw3-p-6">
									<h3 class="tw3-mb-4 tw3-text-base tw3-font-semibold">
										Review your booking
									</h3>
									<dl
										class="tw3-m-0 tw3-grid tw3-gap-3 [&>div:last-child]:tw3-border-0 [&>div:last-child]:tw3-pb-0"
									>
										<div
											class="tw3-flex tw3-justify-between tw3-gap-4 tw3-border-b tw3-border-surface-100 tw3-pb-3"
										>
											<dt class="tw3-text-sm tw3-text-surface-500">Vehicle</dt>
											<dd
												class="tw3-m-0 tw3-text-right tw3-text-sm tw3-font-medium"
											>
												{{ vehicleLabel || selectedVehicle?.vin || '—' }}
											</dd>
										</div>
										<div
											class="tw3-flex tw3-justify-between tw3-gap-4 tw3-border-b tw3-border-surface-100 tw3-pb-3"
										>
											<dt class="tw3-text-sm tw3-text-surface-500">
												Service address
											</dt>
											<dd
												class="tw3-m-0 tw3-text-right tw3-text-sm tw3-font-medium"
											>
												{{ address?.full_address || '—' }}
											</dd>
										</div>
										<div
											class="tw3-flex tw3-justify-between tw3-gap-4 tw3-border-b tw3-border-surface-100 tw3-pb-3"
										>
											<dt class="tw3-text-sm tw3-text-surface-500">
												Preferred date
											</dt>
											<dd
												class="tw3-m-0 tw3-text-right tw3-text-sm tw3-font-medium"
											>
												{{ scheduledDate ? toDateString(scheduledDate) : '—' }}
											</dd>
										</div>
										<div
											class="tw3-flex tw3-justify-between tw3-gap-4 tw3-border-b tw3-border-surface-100 tw3-pb-3"
										>
											<dt class="tw3-text-sm tw3-text-surface-500">Contact</dt>
											<dd
												class="tw3-m-0 tw3-text-right tw3-text-sm tw3-font-medium"
											>
												{{ firstName }} {{ lastName }}
											</dd>
										</div>
										<div
											class="tw3-flex tw3-justify-between tw3-gap-4 tw3-border-b tw3-border-surface-100 tw3-pb-3"
										>
											<dt class="tw3-text-sm tw3-text-surface-500">Phone</dt>
											<dd
												class="tw3-m-0 tw3-text-right tw3-text-sm tw3-font-medium"
											>
												{{ phoneNumber || '—' }}
											</dd>
										</div>
										<div
											class="tw3-flex tw3-justify-between tw3-gap-4 tw3-border-b tw3-border-surface-100 tw3-pb-3"
										>
											<dt class="tw3-text-sm tw3-text-surface-500">Email</dt>
											<dd
												class="tw3-m-0 tw3-text-right tw3-text-sm tw3-font-medium"
											>
												{{ email || '—' }}
											</dd>
										</div>
										<div
											v-if="paymentRequired"
											class="tw3-flex tw3-justify-between tw3-gap-4 tw3-border-b tw3-border-surface-100 tw3-pb-3"
										>
											<dt class="tw3-text-sm tw3-text-surface-500">
												Due today {{ bookingStore.dueTodayPercent }}
											</dt>
											<dd
												class="tw3-m-0 tw3-text-right tw3-text-sm tw3-font-semibold tw3-text-primary-600"
											>
												{{ formatMoney(bookingStore.prepaymentAmount || 0) }}
											</dd>
										</div>
									</dl>
								</BookingCard>
							</div>

							<aside class="lg:tw3-sticky lg:tw3-top-24">
								<BookingQuoteSummary />
							</aside>
						</template>
					</section>
				</template>
			</main>

			<!-- Sticky action bar -->
			<div
				v-if="!submitted && !isLoading"
				class="booking-blurbar tw3-fixed tw3-inset-x-0 tw3-bottom-0 tw3-z-20 tw3-border-t tw3-border-surface-200"
			>
				<div
					class="tw3-mx-auto tw3-flex tw3-max-w-5xl tw3-items-center tw3-justify-between tw3-gap-3 tw3-px-6 tw3-py-3"
				>
					<BookingButton
						:disabled="!canGoBack"
						icon="pi pi-arrow-left"
						label="Back"
						variant="text"
						@click="goBack"
					/>

					<div
						v-if="hasQuote"
						class="tw3-hidden tw3-items-center tw3-gap-5 sm:tw3-flex"
					>
						<div class="tw3-flex tw3-items-baseline tw3-gap-2">
							<span class="tw3-text-xs tw3-text-surface-500">Total</span>
							<span
								class="tw3-text-base tw3-font-semibold tw3-text-surface-700"
							>
								{{ formatMoney(quoteTotal as number) }}
							</span>
						</div>
						<div
							v-if="paymentRequired"
							class="tw3-flex tw3-items-baseline tw3-gap-2"
						>
							<span class="tw3-text-xs tw3-text-surface-500">Due today</span>
							<span class="tw3-text-lg tw3-font-bold tw3-text-primary-600">
								{{ formatMoney(bookingStore.prepaymentAmount || 0) }}
							</span>
						</div>
					</div>

					<BookingButton
						v-if="activeStep === 1"
						:disabled="!canContinueFromVehicle"
						icon="pi pi-arrow-right"
						iconPos="right"
						label="Continue"
						@click="goNext"
					/>
					<BookingButton
						v-else-if="activeStep === 2"
						:disabled="!canContinueFromDetails"
						icon="pi pi-arrow-right"
						iconPos="right"
						label="Continue"
						@click="continueFromDetails"
					/>
					<BookingButton
						v-else
						icon="pi pi-check"
						iconPos="right"
						label="Confirm booking"
						:loading="isSubmitting"
						@click="submitBooking"
					/>
				</div>
			</div>

			<a
				class="tw3-fixed tw3-right-4 tw3-z-10 tw3-flex tw3-items-center tw3-gap-2 tw3-text-[0.65rem] tw3-font-semibold tw3-uppercase tw3-tracking-wide tw3-text-surface-400 tw3-opacity-80 tw3-transition-opacity hover:tw3-opacity-100"
				:class="submitted ? 'tw3-bottom-4' : 'tw3-bottom-20'"
				href="https://glassbiller.com"
				rel="noopener noreferrer"
				target="_blank"
			>
				<span>Powered by</span>
				<img
					alt="GlassBiller"
					class="tw3-h-auto tw3-w-24"
					height="40"
					src="https://web-form.glassbiller.com/img/glassbiller-logo.png"
					width="213"
				/>
			</a>
		</template>
	</div>
</template>

<style scoped lang="scss">
.booking-bg {
	background: radial-gradient(
			1200px 480px at 50% -10%,
			rgb(var(--primary-100) / 0.55),
			transparent 70%
		),
		rgb(var(--surface-50));
}

.booking-blurbar {
	background: rgb(var(--surface-0) / 0.85);
	backdrop-filter: blur(10px);
}

.booking-logo {
	background: linear-gradient(
		135deg,
		rgb(var(--primary-400)),
		rgb(var(--primary-600))
	);
	box-shadow: 0 6px 16px rgb(var(--primary-500) / 0.35);
}

.booking-progress-fill {
	background: linear-gradient(
		90deg,
		rgb(var(--primary-400)),
		rgb(var(--primary-600))
	);
}

:deep(.p-progressspinner-circle) {
	stroke: rgb(var(--primary-500));
	animation: p-progressspinner-dash 1.5s ease-in-out infinite;
}
</style>
